import { useEffect, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Play, X, Check } from "lucide-react";

export default function AdminAuction() {
  const [pool, setPool] = useState([]);
  const [teams, setTeams] = useState([]);
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [bidAmount, setBidAmount] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState("");
  const [showSold, setShowSold] = useState(false);
  const [basePrice, setBasePrice] = useState(200000);

  const load = async () => {
    setPool((await api.get("/auction/pool")).data);
    setTeams((await api.get("/teams")).data);
    setCurrent((await api.get("/auction/current")).data);
    setHistory((await api.get("/auction/history")).data);
  };
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, []);

  const start = async (playerId) => {
    try { await api.post("/admin/auction/start", { player_id: playerId, base_price: basePrice }); toast.success("Auction started"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const bid = async () => {
    if (!selectedTeam || !bidAmount) return toast.error("Select team & amount");
    try { await api.post(`/admin/auction/bid?team_id=${selectedTeam}&amount=${bidAmount}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Bid failed"); }
  };
  const sold = async () => {
    if (!current?.leading_team_id) return toast.error("No leading team");
    try { await api.post("/admin/auction/sold", { team_id: current.leading_team_id, price: current.current_bid }); setShowSold(true); setTimeout(()=>{ setShowSold(false); load(); }, 1500); toast.success("SOLD!"); }
    catch { toast.error("Failed"); }
  };
  const unsold = async () => { await api.post("/admin/auction/unsold"); toast.info("Marked unsold"); load(); };

  return (
    <div className="relative">
      <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Live Auction</div>
      <h1 className="font-display text-4xl mt-2">Auction Ring</h1>

      {current && current.player ? (
        <div className="mt-6 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 relative p-10 bg-gradient-to-br from-[#0B132B] to-[#151F3D] border-2 border-[#39FF14] rounded-lg pulse-neon overflow-hidden" data-testid="auction-current">
            <div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Now Bidding</div>
            <h2 className="font-display text-7xl mt-4">{current.player.name}</h2>
            <div className="mt-2 text-[#FFD700] font-display text-2xl">{current.player.registration_number} • {current.player.role}</div>
            <div className="mt-6 grid grid-cols-4 gap-3">
              {[["Age",current.player.age],["Tehsil",current.player.tehsil],["Jersey",current.player.jersey_size],["Base","₹"+(current.base_price/1000)+"K"]].map(([l,v])=>(
                <div key={l} className="p-3 bg-black/30 border border-white/10">
                  <div className="text-[10px] uppercase text-white/50 tracking-widest">{l}</div>
                  <div className="font-display text-xl mt-1">{v}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex items-end gap-6 flex-wrap">
              <div>
                <div className="text-xs uppercase text-white/50 tracking-widest">Current Bid</div>
                <div className="font-display text-6xl text-[#FFD700]" data-testid="current-bid">₹{(current.current_bid/1000).toFixed(0)}K</div>
              </div>
              <div>
                <div className="text-xs uppercase text-white/50 tracking-widest">Leading</div>
                <div className="font-display text-3xl text-[#39FF14]">{current.leading_team_name || "—"}</div>
              </div>
            </div>
            {showSold && <div className="absolute inset-0 grid place-items-center bg-black/70"><div className="sold-stamp font-display text-9xl text-[#FF2442] border-8 border-[#FF2442] px-10 py-6">SOLD!</div></div>}
          </div>
          <div className="p-6 bg-[#0B132B] border border-white/10 rounded space-y-4">
            <div className="text-xs uppercase tracking-widest text-white/60">Place Bid</div>
            <select value={selectedTeam} onChange={(e)=>setSelectedTeam(e.target.value)} className="w-full bg-black/50 border border-white/15 px-3 py-2 text-sm" data-testid="bid-team">
              <option value="" className="bg-[#0B132B]">Select Team</option>
              {teams.map((t)=><option key={t.id} value={t.id} className="bg-[#0B132B]">{t.name} · ₹{((t.purse-t.spent)/100000).toFixed(1)}L left</option>)}
            </select>
            <input type="number" value={bidAmount} onChange={(e)=>setBidAmount(+e.target.value)} placeholder="Bid amount (₹)" className="w-full bg-black/50 border border-white/15 px-3 py-2 text-sm" data-testid="bid-amount"/>
            <div className="grid grid-cols-3 gap-2">
              {[50000, 100000, 200000].map(inc => (
                <button key={inc} onClick={() => setBidAmount((current.current_bid || 0) + inc)} className="py-2 border border-white/15 text-xs uppercase hover:border-[#39FF14]">+₹{inc/1000}K</button>
              ))}
            </div>
            <button data-testid="place-bid" onClick={bid} className="w-full py-3 bg-[#39FF14] text-black font-bold uppercase tracking-widest flex items-center gap-2 justify-center"><Gavel size={16}/>Place Bid</button>
            <div className="border-t border-white/10 pt-4 grid grid-cols-2 gap-3">
              <button data-testid="mark-sold" onClick={sold} className="py-3 bg-[#FFD700] text-black font-bold uppercase tracking-widest flex items-center gap-2 justify-center text-xs"><Check size={14}/>SOLD</button>
              <button data-testid="mark-unsold" onClick={unsold} className="py-3 bg-[#FF2442] text-white font-bold uppercase tracking-widest flex items-center gap-2 justify-center text-xs"><X size={14}/>UNSOLD</button>
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-6 p-6 bg-[#0B132B] border border-white/10 rounded">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="text-xs uppercase text-white/60 tracking-widest">Set Base Price:</div>
            <input type="number" value={basePrice} onChange={(e)=>setBasePrice(+e.target.value)} className="bg-black/50 border border-white/15 px-3 py-2 text-sm w-40"/>
            <div className="text-xs text-white/50">Pick a player from the pool below to start the bidding.</div>
          </div>
        </div>
      )}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-2xl">Auction Pool ({pool.length})</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {pool.map((p) => (
            <div key={p.id} className="p-4 bg-[#0B132B] border border-white/10 rounded hover:border-[#39FF14]" data-testid={`pool-${p.id}`}>
              <div className="font-display text-lg">{p.name}</div>
              <div className="text-xs text-white/50">{p.registration_number} • {p.tehsil}</div>
              <div className="mt-2 text-xs text-[#FFD700]">{p.role}</div>
              <button data-testid={`start-${p.id}`} onClick={()=>start(p.id)} disabled={!!current?.player} className="mt-3 w-full py-2 bg-[#39FF14] text-black text-xs font-bold uppercase tracking-widest flex items-center gap-1 justify-center disabled:opacity-40"><Play size={12}/>Start</button>
            </div>
          ))}
          {pool.length === 0 && <div className="col-span-full text-white/40 text-sm">No players left in pool.</div>}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="font-display text-2xl mb-3">History</h3>
        <div className="border border-white/10 rounded overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#151F3D] text-white/70"><tr>{["Player","Team","Price","Result","When"].map((h)=><th key={h} className="px-3 py-2 text-left text-xs uppercase tracking-widest">{h}</th>)}</tr></thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.id} className="border-t border-white/5">
                  <td className="px-3 py-2">{h.player_name}</td>
                  <td className="px-3 py-2">{h.team_name || "—"}</td>
                  <td className="px-3 py-2 text-[#FFD700]">{h.price ? `₹${(h.price/1000).toFixed(0)}K` : "—"}</td>
                  <td className="px-3 py-2"><span className={`text-xs px-2 py-1 border ${h.result==="sold"?"border-[#39FF14] text-[#39FF14]":"border-[#FF2442] text-[#FF2442]"}`}>{h.result}</span></td>
                  <td className="px-3 py-2 text-xs text-white/50">{new Date(h.created_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
